import {
  Input,
  Output,
  inject,
  OnChanges,
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  DestroyRef,
  EventEmitter,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  faTimes,
  faChartBar,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import {
  faCirclePlay,
  faCirclePause,
  faCircleStop,
} from '@fortawesome/free-regular-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GridStackNode, GridStackOptions } from 'gridstack';

import { ToastService } from '@services/toast.service';
import { GridsterConfigService } from 'src/app/core/services/gridster-config.service';
import { DashboardAutoRefreshManager } from '../../services/dashboard-auto-refresh-manager';

import {
  Widget,
  Dashboard,
  DashboardPage,
  ContentFilters,
  DashboardPresentationSettings,
} from 'src/app/core/models/core.models';
import { NomObjectClass, WidgetType, ZIndex } from '../../constants';

import {
  selectCoreDarkMode,
  selectCurrentObject,
  selectWidgetsLanguageId,
  getContentFiltersForPage,
  selectEffectivePeriodForPage,
} from '../../reducers';
import { AppState } from 'src/app/reducers';
import {
  injectFetchPresentationWidgets,
  injectFetchWidgetData,
} from 'src/app/api/queries/widget.queries';

import {
  tap,
  map,
  take,
  filter,
  switchMap,
  combineLatest,
  firstValueFrom,
  BehaviorSubject,
  distinctUntilChanged,
  Subject,
} from 'rxjs';

interface PresentationSlide {
  id: number;
  page: DashboardPage;
  widgets: Widget[];
  ready: boolean;
  hasErrors: boolean;
}

@Component({
  selector: 'app-dashboard-presentation-mode',
  templateUrl: './dashboard-presentation-mode.component.html',
  styleUrls: ['./dashboard-presentation-mode.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DashboardPresentationModeComponent
  implements OnChanges, OnDestroy
{
  @Input() isVisible = false;
  @Input() singlePageId: number | null = null;
  @Output() exitPresentation = new EventEmitter<void>();

  @ViewChild('slideContainer', { static: false }) slideContainer!: ElementRef;
  @ViewChild('progressSvg', { static: false }) progressSvg!: ElementRef;

  @HostListener('window:keydown', ['$event'])
  handleKeyPress(event: KeyboardEvent) {
    if (!this.isVisible) return;

    if (event.key === 'Escape') {
      this.exitPresentation.emit();
    } else if (event.key === 'ArrowLeft') {
      this.prevSlide();
    } else if (event.key === 'ArrowRight') {
      this.nextSlide();
    }
  }

  @HostListener('window:resize', ['$event'])
  handleResize(event: Event) {
    if (this.presentationReady && this.slideContainer) {
      // Recalculate slide width on window resize
      this.initializeSlideWidth();
    }
  }

  private currentSlideIndex$ = new BehaviorSubject<number>(0);

  // ! Slides as a controlled BehaviorSubject -> not reactive to Store (for performance reasons)
  slides$ = new BehaviorSubject<PresentationSlide[]>([]);

  currentPageId$ = combineLatest([this.slides$, this.currentSlideIndex$]).pipe(
    map(([slides, i]) => slides[i]?.page?.id ?? null),
    distinctUntilChanged(),
  );

  effectivePeriod$ = this.currentPageId$.pipe(
    switchMap((pageId) =>
      this.store.select(selectEffectivePeriodForPage(pageId)),
    ),
  );

  // Component state
  presentationReady = false;
  currentSlideIndex = 0;

  // * Carousel properties
  slideOffset = 0;
  slideTransition = 'transform 0.3s ease-in-out';
  slideWidth = 0;
  minimumLoadingTime = 1000;

  // Data from store
  dashboard: Dashboard | null = null;
  presentationSettings: DashboardPresentationSettings | null = null;

  // Configuration
  presentationGridConfig: GridStackOptions;
  titleFontSize = 32;
  viewProperties: any = null;

  // Autoplay system
  circularProgress = 0;
  remainingTime = 0;
  private autoplayTimeout: any;
  private progressAnimationId: any;

  // Play State
  isPlaying = true;

  // Track timing for pause/resume
  private timerStartedAt: number | null = null;
  private currentSlideDuration: number = 0;
  private pausedAt: number | null = null;

  faTimes = faTimes;
  faChartBar = faChartBar;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faCirclePlay = faCirclePlay;
  faCircleStop = faCircleStop;
  faCirclePause = faCirclePause;

  ZIndex = ZIndex;

  private dashboardAutoReloadManager = inject(DashboardAutoRefreshManager);
  private destroyRef = inject(DestroyRef);

  autoReloadSecondsLeft$ = this.dashboardAutoReloadManager.secondsLeft$;
  cacheEnabled$ = this.dashboardAutoReloadManager.cacheEnabled$;
  widgetsReloading$ = this.dashboardAutoReloadManager.reloading$;

  private readonly fetchWidgetData = injectFetchWidgetData();
  private readonly fetchPresentationWidgets = injectFetchPresentationWidgets();

  // * the all-pages widget list, fetched once per presentation. Nothing mutates it
  // * while a presentation is open, so a snapshot is the whole story.
  private presentationWidgets: Widget[] = [];

  // * Dark mode state for theme colors
  isDarkMode: boolean = false;
  private noVisiblePagesState$ = new Subject<boolean>();

  constructor(
    private store: Store<AppState>,
    private gridsterConfigService: GridsterConfigService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    // * configure GridStack for Presentation (non-draggable)
    this.presentationGridConfig = {
      ...this.gridsterConfigService.getDashboardConfig(),
      disableDrag: true,
      disableResize: true,
      staticGrid: true,
    };

    // * subscribe to dark mode state
    this.store
      .select(selectCoreDarkMode)
      .pipe(
        tap((darkMode) => {
          this.isDarkMode = darkMode;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * show warning only on state transition: visible -> no visible pages
    this.noVisiblePagesState$
      .pipe(
        distinctUntilChanged(),
        filter((hasNoVisiblePages) => hasNoVisiblePages),
        tap(() => {
          this.toastService.showWarning(
            this.translateService.instant(
              'dashboard-presentation.message.no-visible-pages',
            ),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnChanges() {
    if (this.isVisible && !this.presentationReady) {
      this.initializePresentation();
    } else if (this.isVisible && this.presentationReady) {
      // * Already initialized, but isVisible changed back to true
      this.rebuildSlides();
    } else if (!this.isVisible) {
      this.cleanup();
    }
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private cleanup() {
    this.clearCustomAutoplay();
    this.presentationReady = false;
  }

  // * Rebuild the slides from the fetched widget list.
  private rebuildSlides(): void {
    this.updateSlides(this.createSlidesFromWidgets(this.presentationWidgets));
    this.cdr.detectChanges();
  }

  private selectOnce<T>(selector: any): Promise<T> {
    return firstValueFrom(this.store.select(selector).pipe(take(1)));
  }

  private updateSlides(slides: PresentationSlide[]): void {
    this.slides$.next(slides);
    this.emitNoVisiblePagesState(slides);
  }

  private emitNoVisiblePagesState(slides: PresentationSlide[]): void {
    if (!this.dashboard || this.singlePageId) {
      this.noVisiblePagesState$.next(false);
      return;
    }

    const hasAnyPages = this.dashboard.pages.length > 0;
    this.noVisiblePagesState$.next(hasAnyPages && slides.length === 0);
  }

  private async initializePresentation(): Promise<void> {
    const startTime = Date.now();

    try {
      // ! 1️. get Data from NgRx store (already hydrated!)
      const currentObject = await this.selectOnce(selectCurrentObject);
      this.dashboard = currentObject as Dashboard;
      this.presentationSettings = this.dashboard.presentation_settings;
      this.titleFontSize = this.presentationSettings.page_title_font_size;

      // ! 2️. load all Widgets metadata for Presentation mode
      await this.loadAllWidgetsForPresentation();

      // ! 3️. create Slides from the snapshot and push to BehaviorSubject
      const slides = this.createSlidesFromWidgets(this.presentationWidgets);
      this.updateSlides(slides);

      // ! 5️. prefetch FIRST slide data only (awaited, so the loader stays up
      // ! until the first slide has charts)
      if (slides.length > 0) {
        await this.prefetchPageBatch(slides[0]);
      }

      // ! 6️. ensure loader shows for at least 1 seconds
      const elapsed = Date.now() - startTime;
      if (elapsed < this.minimumLoadingTime) {
        await new Promise((resolve) =>
          setTimeout(resolve, this.minimumLoadingTime - elapsed),
        );
      }

      // ! 7️. initialize starting Slide index
      this.currentSlideIndex = 0;
      this.currentSlideIndex$.next(0);

      // ! 8️. ready to present (first Slide has charts, rest hydrate progressively)
      this.presentationReady = true;
      this.cdr.detectChanges();

      // ! 9️. hand the widget list to the auto-refresh manager (a service cannot
      // ! hold the query hook, so it receives the value)
      this.dashboardAutoReloadManager.setPresentationWidgets(
        this.presentationWidgets,
      );

      // ! 10. initialize custom carousel
      this.initializeCustomCarousel();

      // ! 11. prefetch remaining Slides in background (progressive updates)
      if (slides.length > 1) {
        this.prefetchRemainingSlides(slides.slice(1));
      }
    } catch (error) {
      this.toastService.showError(
        this.translateService.instant(
          'dashboard-presentation.message.failed-initialize-presentation',
        ),
      );
    }
  }

  // * Prefetch remaining slides sequentially. No slide rebuild afterwards: the
  // * cards observe the query cache directly.
  private async prefetchRemainingSlides(
    slides: PresentationSlide[],
  ): Promise<void> {
    for (const slide of slides) {
      await this.prefetchPageBatch(slide);
    }
  }

  // * Prefetch all Widgets for a single Page in parallel, into the query cache the
  // * cards observe - they stay disabled, see widget-card's `prefetchedExternally`
  private async prefetchPageBatch(slide: PresentationSlide): Promise<void> {
    const widgets = slide.widgets.filter(
      (w) => w.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
    );
    if (widgets.length === 0) return;

    // ! Get Content Filters and language for this page
    const [contentFilters, languageId] = await Promise.all([
      this.selectOnce(getContentFiltersForPage(slide.page.id)),
      this.selectOnce(selectWidgetsLanguageId),
    ]);

    // * Fetch all Widgets data for this Page in parallel
    await Promise.all(
      widgets.map((widget) =>
        this.fetchWidgetData({
          widgetId: widget.id,
          contentFilters: contentFilters as ContentFilters,
          languageId: languageId as number,
          // force-enable cache upon prefetching (BE sets the cache after each search, regardless of the flag, so it is safe to load from cache)
          cacheEnabled: true,
        }).catch((err) => {
          console.warn(`Widget ${widget.id} (${widget.title}) failed:`, err);
          // ! Continue on error
          return null;
        }),
      ),
    );
  }

  // * Load ALL widgets for Presentation Mode - its own query key (no page_id)
  private async loadAllWidgetsForPresentation(): Promise<void> {
    if (!this.dashboard) {
      this.toastService.showError(
        this.translateService.instant(
          'dashboard-presentation.message.dashboard-not-found',
        ),
      );
      return;
    }

    // ! a rejection propagates to initializePresentation's catch, which toasts,
    // ! rather than presenting empty slides
    this.presentationWidgets = await this.fetchPresentationWidgets({
      objectId: this.dashboard.id,
      objectClassId: NomObjectClass.DASHBOARD,
    });
  }

  private createSlidesFromWidgets(widgets: Widget[]): PresentationSlide[] {
    if (!this.dashboard) return [];

    let pagesToShow: DashboardPage[];

    if (this.singlePageId) {
      // * Single page mode: bypass visibility filter and show specific page
      const singlePage = this.dashboard.pages.find(
        (p) => p.id === this.singlePageId,
      );
      pagesToShow = singlePage ? [singlePage] : [];
    } else {
      // * Normal presentation: only show visible pages
      pagesToShow = this.getVisiblePages(this.dashboard.pages);
    }

    return pagesToShow.map((page) => {
      const pageWidgets = widgets.filter(
        (widget) => widget.page_id === page.id,
      );

      return {
        id: page.id,
        page: page,
        widgets: pageWidgets,
        ready: true,
        hasErrors: false,
      } as PresentationSlide;
    });
  }

  private initializeCustomCarousel() {
    if (!this.slideContainer) return;

    // * initialize slide width and position
    this.initializeSlideWidth();

    const isAuto = this.presentationSettings?.mode === 'auto';

    // * start Auto mode if enabled
    if (isAuto) {
      this.restartTimerFor(this.currentSlideIndex);
    }
  }

  private restartTimerFor(index: number) {
    this.clearCustomAutoplay();
    this.pausedAt = null;

    this.slides$
      .pipe(
        take(1),
        tap((slides) => {
          const delayMs = this.getSlideDurationByIndex(index, slides) * 1000;
          this.currentSlideDuration = delayMs;
          this.timerStartedAt = Date.now();

          if (this.isPlaying) {
            this.updateProgressAnimation(delayMs, 0);

            this.autoplayTimeout = setTimeout(() => {
              // * get fresh slides when timer fires
              this.slides$
                .pipe(
                  take(1),
                  tap((currentSlides) => {
                    const isLast = index >= currentSlides.length - 1;

                    if (isLast) {
                      // ! instant wrap to first slide for infinite loop
                      this.goToSlide(0, true);
                    } else {
                      // ! normal forward animation
                      this.nextSlide();
                    }
                  }),
                )
                .subscribe();
            }, delayMs);
          } else {
            // When paused, show full duration
            this.showFullDuration(delayMs);
          }
        }),
      )
      .subscribe();
  }

  private getSlideDurationByIndex(
    index: number,
    slides: PresentationSlide[],
  ): number {
    const slide = slides[index];

    return slide?.page.duration_seconds;
  }

  private clearCustomAutoplay() {
    if (this.autoplayTimeout) {
      clearTimeout(this.autoplayTimeout);
      this.autoplayTimeout = null;
    }
    if (this.progressAnimationId) {
      cancelAnimationFrame(this.progressAnimationId);
      this.progressAnimationId = null;
    }
  }

  private updateProgressAnimation(
    duration: number,
    alreadyElapsed: number = 0,
  ) {
    if (!this.showTimer) return;

    const startTime = Date.now() - alreadyElapsed;

    const animate = () => {
      if (!this.isPlaying) return; // ! Stop animating when paused

      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      this.circularProgress = 1 - progress;
      this.remainingTime = Math.ceil((duration - elapsed) / 1000);

      // Update circular progress
      if (this.progressSvg?.nativeElement) {
        const circle = this.progressSvg.nativeElement.querySelector('circle');
        if (circle) {
          // * 125.6 == 2 * Math.PI * 20 (radius) - see the template.
          // * It can be defined with variables but only if we need to reuse it (as component) - otherwise it is overkill.
          const offset = 125.6 * (1 - this.circularProgress);
          circle.style.strokeDashoffset = offset.toString();
        }
      }

      this.cdr.detectChanges();

      if (progress < 1) {
        this.progressAnimationId = requestAnimationFrame(animate);
      }
    };

    this.progressAnimationId = requestAnimationFrame(animate);
  }

  private showFullDuration(duration: number) {
    if (!this.showTimer) return;

    // Set progress to full (100%)
    this.circularProgress = 1;
    this.remainingTime = Math.ceil(duration / 1000);

    // Draw the full circle
    if (this.progressSvg?.nativeElement) {
      const circle = this.progressSvg.nativeElement.querySelector('circle');
      if (circle) {
        // Full circle = offset 0
        circle.style.strokeDashoffset = '0';
      }
    }

    this.cdr.detectChanges();
  }

  private getVisiblePages(pages: DashboardPage[]): DashboardPage[] {
    if (!pages.length) return pages;

    // * Filter pages by is_visible_in_presentation flag
    const filteredPages = pages
      .filter((page) => page.is_visible_in_presentation === true)
      .sort((a, b) => a.page_order - b.page_order);

    return filteredPages;
  }

  // * Template methods - Start
  get isAutoMode(): boolean {
    return this.presentationSettings?.mode === 'auto';
  }

  get isManualMode(): boolean {
    return this.presentationSettings?.mode === 'manual';
  }

  get showTimer(): boolean {
    return this.presentationSettings?.show_timer || false;
  }

  trackBySlideId(index: number, slide: PresentationSlide): number {
    return slide.id;
  }

  trackByWidgetId(index: number, widget: Widget): string {
    return widget.id;
  }

  private readonly gridItemOptionsCache = new Map<string, GridStackNode>();

  // * Must return a reference-stable object for unchanged geometry: the
  // * gridstack-item [options] setter calls grid.update() whenever the input
  // * reference changes, and this is invoked from the template on every
  // * change-detection cycle.
  getGridItemOptions(widget: Widget): GridStackNode {
    const cached = this.gridItemOptionsCache.get(widget.id);
    if (
      cached &&
      cached.x === widget.x &&
      cached.y === widget.y &&
      cached.w === widget.cols &&
      cached.h === widget.rows
    ) {
      return cached;
    }

    const options: GridStackNode = {
      x: widget.x,
      y: widget.y,
      w: widget.cols,
      h: widget.rows,
      id: widget.id,
      noResize: true,
      noMove: true,
    };
    this.gridItemOptionsCache.set(widget.id, options);
    return options;
  }

  // * Custom carousel navigation methods
  private calculateSlideOffset(slideIndex: number): number {
    return -(slideIndex * this.slideWidth);
  }

  private updateSlidePosition(instant = false): void {
    this.slideOffset = this.calculateSlideOffset(this.currentSlideIndex);
    this.slideTransition = instant ? 'none' : 'transform 0.3s ease-in-out';
    this.cdr.detectChanges();
  }

  private initializeSlideWidth(): void {
    if (this.slideContainer?.nativeElement) {
      this.slideWidth = this.slideContainer.nativeElement.offsetWidth;
      this.updateSlidePosition(true); // Initial position without animation
    }
  }

  goToSlide(index: number, instant = false): void {
    this.slides$
      .pipe(
        take(1),
        tap((slides) => {
          if (index < 0 || index >= slides.length) return;

          const slideChanged = index !== this.currentSlideIndex;
          this.currentSlideIndex = index;
          this.currentSlideIndex$.next(index);
          this.updateSlidePosition(instant);

          // * in Auto mode, restart timer for the new slide
          if (this.isAutoMode) {
            // ! If Paused and slide changed -> reset timing state to show full duration
            if (!this.isPlaying && slideChanged) {
              this.pausedAt = null;
              this.timerStartedAt = null;
            }
            this.restartTimerFor(this.currentSlideIndex);
          }
        }),
      )
      .subscribe();
  }

  nextSlide(instant = false): void {
    if (this.singlePageId) return; // * Disable navigation in single page mode

    this.slides$
      .pipe(
        take(1),
        tap((slides) => {
          const nextIndex = this.currentSlideIndex + 1;
          if (nextIndex >= slides.length) {
            // * instant wrap to first slide for seamless infinite loop (both auto and manual)
            this.goToSlide(0, true);
          } else {
            this.goToSlide(nextIndex, instant);
          }
        }),
      )
      .subscribe();
  }

  prevSlide(instant = false): void {
    if (this.singlePageId) return; // * Disable navigation in single page mode

    this.slides$
      .pipe(
        take(1),
        tap((slides) => {
          const prevIndex = this.currentSlideIndex - 1;
          if (prevIndex < 0) {
            // * instant wrap to last slide for seamless infinite loop
            this.goToSlide(slides.length - 1, true);
          } else {
            this.goToSlide(prevIndex, instant);
          }
        }),
      )
      .subscribe();
  }

  // * Presentation controls methods
  togglePlayPause() {
    if (!this.isAutoMode) return;

    this.isPlaying = !this.isPlaying;

    if (this.isPlaying) {
      this.resumeFromPause();
    } else {
      this.pausePresentation();
    }
  }

  private pausePresentation() {
    this.pausedAt = Date.now();

    // * stop timers but keep Progress visible
    if (this.autoplayTimeout) {
      clearTimeout(this.autoplayTimeout);
      this.autoplayTimeout = null;
    }
    if (this.progressAnimationId) {
      cancelAnimationFrame(this.progressAnimationId);
      this.progressAnimationId = null;
    }
  }

  private resumeFromPause() {
    if (!this.pausedAt || !this.timerStartedAt) {
      // ! Fallback: restart from beginning if we do not have timing info
      this.restartTimerFor(this.currentSlideIndex);
      return;
    }

    const elapsed = this.pausedAt - this.timerStartedAt;
    const remaining = Math.max(0, this.currentSlideDuration - elapsed);

    this.pausedAt = null;
    // * adjust start time
    this.timerStartedAt = Date.now() - elapsed;

    // * continue animation with FULL duration but already elapsed time
    this.updateProgressAnimation(this.currentSlideDuration, elapsed);

    this.autoplayTimeout = setTimeout(() => {
      this.slides$
        .pipe(
          take(1),
          tap((currentSlides) => {
            const isLast = this.currentSlideIndex >= currentSlides.length - 1;
            if (isLast) {
              this.goToSlide(0, true);
            } else {
              this.nextSlide();
            }
          }),
        )
        .subscribe();
    }, remaining);
  }

  onStopClick() {
    this.exitPresentation.emit();
  }

  // * Helper method to resolve theme color
  getThemeColor(themeColors: { light: string; dark: string }): string {
    return this.isDarkMode ? themeColors?.dark : themeColors?.light;
  }

  get logoUrl(): string {
    return this.isDarkMode ? 'assets/img/logo.svg' : 'assets/img/logo.png';
  }
}
